import { TableCell, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';

// formStyle className constant (passed to <Form formStyle={...}>) — see TaskTypeRoot wrapper.
export const TT_FORM_STYLE = 'task-types-form-style';

/** Wrapper applied around TaskTypeEditor/FieldBuilder/etc. — encapsulates Form className styles. */
export const TaskTypeRoot = styled('div')(({ theme }) => ({
  [`& .${TT_FORM_STYLE}`]: {
    maxWidth: 'none',
    [theme.breakpoints.down('sm')]: {
      '& .MuiFormControl-root': {
        width: '100%',
      },
    },
  },
}));

export const TaskTypeContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  position: 'relative',
  padding: isMobile ? 0 : theme.spacing(1),
  '& .MuiTableCell-root': {
    fontSize: 14,
    padding: theme.spacing(1, 2),
  },
  '& tbody tr:last-child td': {
    borderBottom: 'none',
  },
}));

export const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.secondary,
  fontSize: '13px !important',
  borderBottom: `2px solid ${theme.palette.divider}`,
}));

export const EnabledRow = styled(TableRow)(({ theme }) => ({
  '& td': { color: theme.palette.text.primary },
}));

export const DisabledRow = styled(TableRow)(({ theme }) => ({
  '& td': {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
}));

export const DisabledTextCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontStyle: 'italic',
}));

export const SafetyNote = styled('div')(({ theme }) => ({
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
  fontSize: '13px !important',
  padding: theme.spacing(2),
}));

export const TaskTypeTable = styled('div')({
  width: '100%',
  '& table': { width: '100%' },
  '& td': { fontSize: 12 },
  '& tr': { cursor: 'pointer' },
});

export const EditorContent = styled('div')(({ theme }) => ({
  padding: theme.spacing(3, 4),
  maxWidth: 800,
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5),
    maxWidth: '100%',
    overflowX: 'hidden',
  },
}));

export const Subtitle = styled('div')(({ theme }) => ({
  fontSize: '14px !important',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(3),
}));

export const Section = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    overflowX: 'auto',
  },
}));

export const SectionTitle = styled('div')(({ theme }) => ({
  fontSize: '16px !important',
  fontWeight: 'bold',
  marginBottom: theme.spacing(1),
}));

// Fields table
export const FieldsHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.secondary,
  fontSize: '13px !important',
  borderBottom: `2px solid ${theme.palette.divider}`,
  paddingBottom: theme.spacing(0.5),
}));

export const FieldsRow = styled(TableRow)(({ theme }) => ({
  '& td': {
    fontSize: 14,
    padding: theme.spacing(0.5, 1),
  },
}));

export const DragHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
  cursor: 'grab',
  display: 'flex',
  alignItems: 'center',
}));

export const TypeLabel = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.5px',
  color: theme.palette.text.secondary,
}));

export const TypeLabelDisabled = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.5px',
  color: theme.palette.text.disabled,
}));

export const ActionIcons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 2,
});

export const AddFieldBtn = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

export const RequiredNote = styled('div')(({ theme }) => ({
  fontSize: '12px !important',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  marginTop: theme.spacing(1),
}));

export const CloseoutRow = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
  padding: theme.spacing(1, 2),
}));

export const FieldPickerGrid = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
}));

export const ConfigForm = styled('div')(({ theme }) => ({
  minWidth: 400,
  padding: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    minWidth: 'unset',
    width: '100%',
  },
}));

export const FloatingButtons = styled('div')(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: theme.spacing(1),
  zIndex: 1300,
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(2),
    width: 'calc(100% - 32px)',
    justifyContent: 'center',
  },
}));

// Note: the original `cancelBtn` style added grey background + hover. We pass it via the
// Use global Button `color` / `variant` props (MUI standard) at call sites.
